import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiPath from '../../utils/apiPath';

/* fetch dynamic fields */
export const fetchDynamicFields = createAsyncThunk(
  "dynamicFields",
  async (_, { rejectWithValue }) => { 
    try {
      const response = await apiPath.get(`/api/v1.0/dynamicids/client-side/blocknfield-ids`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* fetch geofence alerts */
export const fetchGeofenceAlerts = createAsyncThunk(
  "geofenceAlerts",
  async ({ start_date, end_date }, { rejectWithValue }) => {
    try {
      const response = await apiPath.get("/api/v1.0/geofence/geofenceAlertslists", {
        params: {
          ...(start_date && { start_date }),
          ...(end_date && { end_date }),
        },
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? error.message);
    }
  }
);

/* Combined fetch for unassigned bookings - vendors and vehicles */
export const fetchUnassignedBookings = createAsyncThunk(
  "unassignedBookings",
  async ({ type, start_date, end_date, page = 1, limit = 10 }, { rejectWithValue }) => { 
    try {
      // Build query parameters
      const params = new URLSearchParams({
        page: page,
        limit: limit,
        ...(start_date && { start_date }),
        ...(end_date && { end_date }),
        ...(type === 'vehicle' && { vehicle: 'unassigned' })
      });

      const response = await apiPath.get(`/api/v1.0/reports/unassigned_bookings?${params}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* fetch bookings */
export const fetchBookings = createAsyncThunk(
  "bookings",
  async ({ date, filter, clientId, vendorId }, { rejectWithValue }) => { 
    try {
      const response = await apiPath.get( `/api/v1.0/reports/bookings?client_id=${clientId}${vendorId ? `&vendor_id=${vendorId}` : ''}${filter ? `&${filter}=${date}` : ''}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* fetch vehicles  */
export const fetchVehicles = createAsyncThunk(
  "vehicles",
  async ({ clientId }, { rejectWithValue }) => { 
    try {
      const response = await apiPath.get(`/api/v1.0/reports/vehicles?client_id=${clientId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* fetch earnings per STP */
export const fetchEarningsPerStp = createAsyncThunk(
  "fetchEarningsPerStp",
  async ({ clientId,start_date,end_date }, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/rfidledger/earnings-per-stp?client_id=${clientId}${start_date?`&start_date=${start_date}`:''}${end_date?`&end_date=${end_date}`:''}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* fetch vehicles per STP */
export const fetchVehiclesPerStp = createAsyncThunk(
  "fetchVehiclesPerStp",
  async ({ clientId,start_date,end_date  }, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/rfidledger/vehicles-per-stp?client_id=${clientId}${start_date?`&start_date=${start_date}`:''}${end_date?`&end_date=${end_date}`:''}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* fetch Total earning */
export const fetchTotalEarning = createAsyncThunk(
  "total-earning",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/reports/total-earning`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* fetch RFID balances */
export const fetchRfidBalances = createAsyncThunk(
  "rfid-balances",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/reports/rfid-balance`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* fetch RFID balances */
export const fetchActivityLogs = createAsyncThunk(
  "activity-logs",
  async (date,{ rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/activityLog/activityFeed?date_range=${date}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchHeatMapData = createAsyncThunk(
  "heatMapData",
  async (dateRange, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/heat-map/reports?date_range=${dateRange.startDate}To${dateRange.endDate}`);
      return response.data?.result || [];
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchMonthlyStpRevenue = createAsyncThunk(
  "monthlyStpRevenue",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/reports/monthly-stp-revenue`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchCapacityPerStp = createAsyncThunk(
  "fetchCapacityPerStp",
  async ({ start_date, end_date }, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(
        `/api/v1.0/reports/capacity-per-stp?date_range=${start_date} TO ${end_date}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* fetch cancelled bookings by vendors */
export const fetchCancelBookings = createAsyncThunk(
  "cancelBookings",
  async ({ date_range }, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(
        `/api/v1.0/reports/cancel-bookings?date_range=${date_range}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* fetch STP bookings */
export const fetchStpBookings = createAsyncThunk(
  "stpBookings",
  async (
    { page = 1, limit = 20, stp_id, start_date, end_date },
    { rejectWithValue }
  ) => {
    try {
      // const params = new URLSearchParams({
      //   page,
      //   limit,
      //   ...(stp_id && { stp_id }),
      //   ...(date_range && { start_date }+'TO'+{ end_date }),
      //   // ...(end_date && { end_date }),
      // });

      const response = await apiPath.get(
        `/api/v1.0/reports/stp-bookings?page=${page}&limit=${limit}${stp_id ? `&stpId=${stp_id}` : ''}${start_date && end_date ? `&date_range=${start_date}TO${end_date}` : ''}`
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* fetch unwanted vehicles */
export const fetchUnwantedVehicles = createAsyncThunk(
  "unwantedVehicles",
  async ({ date_range, vehicle_number }, { rejectWithValue }) => {
    try {
      const params = {
        date_range
      };
      if (vehicle_number) {
        params.vehicle_number = vehicle_number;
      }
      const response = await apiPath.get(`/api/v1.0/reports/unwanted-vehicle`,{ params }
      );
      
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* fetch No GPS Vehicles */
export const fetchNoGpsVehicles = createAsyncThunk(
  "noGpsVehicles",
  async ({ start_date, end_date }, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(
        `/api/v1.0/geofence/no-gps-vehicle?dateRange=${start_date}TO${end_date}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* fetch Trip Geo Tag Verification */
// export const fetchTripGeoTagVerification = createAsyncThunk(
//   "tripGeoTagVerification",
//   async ({ start_date, end_date, page = 1, limit = 5 }, { rejectWithValue }) => {
//     try {
//       const response = await apiPath.get(
//         `/api/v1.0/reports/trip-verification/distance?date_range=${start_date}TO${end_date}&page=${page}&limit=${limit}`
//       );
//       return response.data;
//     } catch (error) {
//       return rejectWithValue(error.response?.data || error.message);            
//     }
//   }
// );
export const fetchTripGeoTagVerification = createAsyncThunk(
  "tripGeoTagVerification",
  async (
    { start_date, end_date, distance_range, page = 1, limit = 5 },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiPath.get(
        `/api/v1.0/reports/trip-verification/distance`,
        {
          params: {
            date_range:`${start_date}TO${end_date}`,
            distance_range,
            page,
            limit,
          },
        }
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);





/* Report Slice @start */
const reportSlice = createSlice({
  name: "report",
  initialState: {
    vehicles: [],
    bookings: [],
    earningsPerStp: [],
    heatMapData: [], 
    vehiclesPerStp: [],
    unassignedBookings:[],
    rfidBalances:[],
    activityLogs:[], 
    capacityPerStp: [],
    geofenceAlerts: [],
    noGpsVehicles: [],
    tripGeoTagVerification: [],
    cancelBookings: [],
    dynamicFields: {}, 
    loading: false,
    fieldsLoading: false, 
    totalBookingRecords: 0,
    unwantedVehicles: [],
    totalRecord:0,
    monthlyStpRevenue: [],
    stpBookings: [],
    totalEarning:0,
    error: null

  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearUnassignedVendors: (state) => {
      state.unassignedVendors = [];
      state.totalVendorRecords = 0;
    },
    clearDynamicFields: (state) => {
      state.dynamicFields = null;
    }
  },
  extraReducers: (builder) => {
    builder
      /* Fetch Dynamic Fields */
      .addCase(fetchDynamicFields.pending, (state) => {
        state.fieldsLoading = true;
        state.error = null;
      })
      .addCase(fetchDynamicFields.fulfilled, (state, action) => {
        state.fieldsLoading = false;
        state.error = null;
        state.dynamicFields = action.payload.blockFields;
      })
      .addCase(fetchDynamicFields.rejected, (state, action) => {
        state.fieldsLoading = false;
        state.error = action.payload || action.error.message;
        state.dynamicFields = null;
      })
      
      /* Fetch Geofence Alerts */
      .addCase(fetchGeofenceAlerts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGeofenceAlerts.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.geofenceAlerts = action.payload.alerts || [];
      })
      .addCase(fetchGeofenceAlerts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || action.error;
        state.geofenceAlerts = [];
      })
      
      /* Fetch Vehicles */
      .addCase(fetchVehicles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchVehicles.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.vehicles = action.payload.vehicles;
      })
      .addCase(fetchVehicles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      /* Fetch Cancel Bookings */
    .addCase(fetchCancelBookings.pending, (state) => {
      state.loading = true;
      state.error = null;
    })
    .addCase(fetchCancelBookings.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
      state.cancelBookings = action.payload.result || [];
    })
    .addCase(fetchCancelBookings.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || action.error.message;
      state.cancelBookings = [];
      })

      /* Fetch Total Earning */
      .addCase(fetchTotalEarning.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTotalEarning.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.totalEarning = action.payload.totalEarnings;
      })
      .addCase(fetchTotalEarning.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      /* Fetch Bookings */
      .addCase(fetchBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.bookings = action.payload.bookings;
      })
      .addCase(fetchBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      /* Fetch RFID balances */
      .addCase(fetchRfidBalances.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRfidBalances.fulfilled, (state, action) => {
        state.loading = false;
        state.rfidBalances = action.payload.records;
      })
      .addCase(fetchRfidBalances.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })
      
      /* Fetch Unwanted Vehicles */
      .addCase(fetchUnwantedVehicles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnwantedVehicles.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        // API के response structure के according adjust करें
        state.unwantedVehicles = action.payload.vehicles || action.payload.result || [];
      })
      .addCase(fetchUnwantedVehicles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        state.unwantedVehicles = [];
      })

      /* Combined Fetch Unassigned Bookings */
      .addCase(fetchUnassignedBookings.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUnassignedBookings.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.totalBookingRecords = action.payload.totalRecord || 0;
        state.unassignedBookings = action.payload.bookings || [];
      })
      .addCase(fetchUnassignedBookings.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        state.unassignedBookings = [];
      })
      
      /* Fetch Earnings per STP */
      .addCase(fetchEarningsPerStp.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchEarningsPerStp.fulfilled, (state, action) => {
        state.loading = false;
        state.earningsPerStp = action.payload.earnings || [];
      })
      .addCase(fetchEarningsPerStp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        state.earningsPerStp = [];
      })
      
      /* Fetch Activity logs */
      .addCase(fetchActivityLogs.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchActivityLogs.fulfilled, (state, action) => {
        state.loading = false;
        state.activityLogs = action.payload.activity || [];
      })
      .addCase(fetchActivityLogs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        state.activityLogs = [];
      })
      
      /* Fetch Vehicles per STP */
      .addCase(fetchVehiclesPerStp.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchVehiclesPerStp.fulfilled, (state, action) => {
        state.loading = false;
        state.vehiclesPerStp = action.payload.vehicles || [];
      })
      .addCase(fetchVehiclesPerStp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        state.vehiclesPerStp = [];
      })
      /* Fetch Capacity per STP */
      .addCase(fetchCapacityPerStp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCapacityPerStp.fulfilled, (state, action) => {
        state.loading = false;
        state.capacityPerStp = action.payload.stpCapacity || [];
      })
      .addCase(fetchCapacityPerStp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        state.capacityPerStp = [];
      })
      /* Fetch Heat Map Data */
      .addCase(fetchHeatMapData.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchHeatMapData.fulfilled, (state, action) => {
        state.loading = false;
        state.heatMapData = action.payload; 
      })
      .addCase(fetchHeatMapData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        state.heatMapData = [];
      })
      /* Fetch Monthly STP Revenue */
      .addCase(fetchMonthlyStpRevenue.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchMonthlyStpRevenue.fulfilled, (state, action) => {
        state.loading = false;
        state.monthlyStpRevenue = action.payload.totalSTPEarnings || [];
      })
      .addCase(fetchMonthlyStpRevenue.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
        state.monthlyStpRevenue = [];
      })
      /* Fetch No GPS Vehicles */
.addCase(fetchNoGpsVehicles.pending, (state) => {
  state.loading = true;
  state.error = null;
})
.addCase(fetchNoGpsVehicles.fulfilled, (state, action) => {
  state.loading = false;
  state.error = null;
  state.noGpsVehicles = action.payload.vehicles || action.payload.result || [];
})
.addCase(fetchNoGpsVehicles.rejected, (state, action) => {
  state.loading = false;
  state.error = action.payload || action.error.message;
  state.noGpsVehicles = [];
})

        /* Fetch STP Bookings */
  .addCase(fetchStpBookings.pending, (state) => {
    state.loading = true;
    state.error = null;
  })
  .addCase(fetchStpBookings.fulfilled, (state, action) => {
    state.loading = false;
    state.error = null;
    state.stpBookings = action.payload.bookings || [];
    state.totalStpBookingRecords = action.payload.totalRecord || 0;
    state.totalStpBookingPages = action.payload.totalPage || 0;
    state.currentStpBookingPage = action.payload.currentPage || 1;
  })
  .addCase(fetchStpBookings.rejected, (state, action) => {
    state.loading = false;
    state.error = action.payload || action.error.message;
    state.stpBookings = [];
  })
  
 /* Fetch Trip Geo Tag Verification */
.addCase(fetchTripGeoTagVerification.pending, (state) => {
  state.loading = true;
  state.error = null;
})

.addCase(fetchTripGeoTagVerification.fulfilled, (state, action) => {
  state.loading = false;
  state.error = null;

  state.tripGeoTagVerification =
    action.payload.data ||
    action.payload.result ||
    action.payload.records ||
    [];

  // ✅ FIXED: match backend response key
  state.totalTripGeoTagRecords =
    action.payload.totalRecord || 0;
})


.addCase(fetchTripGeoTagVerification.rejected, (state, action) => {
  state.loading = false;
  state.error = action.payload || action.error.message;
  state.tripGeoTagVerification = [];
  state.totalTripGeoTagRecords = 0;
});

        }
      });

export const { clearError, clearUnassignedVendors, clearUnassignedVehicles, clearDynamicFields } = reportSlice.actions;
export default reportSlice.reducer;